import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import LuckyDaysLogo from '@/components/UI/Logos/LuckyDaysLogo';
import { MARKET_CLOSURE_FROM } from '@/constants/constants';

// LP-1281: luckydays.com markets that stay live after the .com sale.
export const MARKET_CLOSURE_OPEN_LOCALES = ['fi', 'fi-en'];

export const isMarketClosureActive = (now: number = Date.now()): boolean => {
  if (!MARKET_CLOSURE_FROM) {
    return false;
  }

  const from = Date.parse(MARKET_CLOSURE_FROM);

  return !Number.isNaN(from) && now >= from;
};

// Plain bold text, deliberately not a link (QA on LP-1281).
const NEW_HOME = <strong>luckydays.co</strong>;

// Copy is verbatim from the LP-1281 sheet ("LD - EN" / "LD - DE" columns).
const COPY = {
  en: {
    lang: 'en',
    heading: 'Loading luck....',
    paragraphs: [
      <>We are temporarily offline for system maintenance. Your data is safe and intact.</>,
      <>We’ll be back online shortly to welcome you at {NEW_HOME}</>,
      <>Thanks for your patience!</>,
    ],
  },
  de: {
    lang: 'de',
    heading: 'Glück wird geladen...',
    paragraphs: [
      <>
        Wir sind gerade im Maintenance-Modus für dich! Deine Daten sind bei uns in absolut sicheren
        Händen!
      </>,
      <>Wir sind ganz schnell wieder online und heißen dich auf {NEW_HOME} herzlich willkommen.</>,
      <>Vielen Dank für deine Geduld!</>,
    ],
  },
};

interface MarketClosurePageProps {
  locale: string;
}

export function MarketClosurePage({ locale }: MarketClosurePageProps): JSX.Element {
  const copy = locale === 'at' ? COPY.de : COPY.en;

  useEffect(() => {
    // Remove the index.html loader, normally done by Internationalisation which we bypass here.
    const loading = document.getElementById('loading');
    if (loading !== null) {
      loading.outerHTML = '';
    }
  }, []);

  return (
    <>
      <Helmet>
        <html lang={copy.lang} />
        <title>LuckyDays</title>
      </Helmet>
      <main className="flex min-h-screen items-center justify-center bg-[#007078] px-8 text-center text-white">
        <div className="w-full max-w-[440px]">
          <div className="mb-6 flex justify-center" role="img" aria-label="LuckyDays">
            <LuckyDaysLogo width={148} height={31} />
          </div>
          <h1 className="mb-4 text-3xl font-bold">{copy.heading}</h1>
          {copy.paragraphs.map((paragraph, index) => (
            <p key={index} className="mb-4 text-base xs:text-lg">
              {paragraph}
            </p>
          ))}
        </div>
      </main>
    </>
  );
}
